import classnames from 'classnames';

import type { JSONContent } from '@tiptap/core';
import type { CSSProperties } from 'react';
import type { SerializableNode } from '../serializer';
import type { Author } from '../types';
import { computeFont } from '../utils/fonts';
import * as styles from './Authors.css';
import type { AuthorsElementProps, TAuthorAttributes } from './Authors.type';
import Serializer from './Serializer';
import { AuthorSerializer } from './SerializerV2';
import RedirectLink from './components/RedirectLink';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

export function AuthorsElement(props: AuthorsElementProps) {
  const { element, children, attributes, className, isLive = false } = props;
  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    gap,
    alignment,
    shadow,
    columns,
    backgroundColor,
    cardBorderWidth,
    cardBorderRadius,
    cardBorderColor,
    cardBorderStyle,
    cardPadding,
    cardBackgroundColor,
    cardTextColor,
    cardBioColor,
    cardSocialFill,
    nameFontFamily,
    nameFontSize,
    nameTextTransform,
    bioFontFamily,
    bioFontSize,
    bioTextTransform,
    shouldNavigateToAuthorPage,
    data = [],
    cardStructureV2,
    structureTiptapState,
  } = element.attrs || {};

  const authors = Array.isArray(data) ? [] : data?.authors || [];

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        customAttributes?.className,
        'relative',
        'grid',
        'w-full',
        COLUMNS_MAP[columns || '3'],
      )}
      style={
        {
          gap: gap,
          '--padding': padding,
          '--background-color': backgroundColor,
          '--name-text-color': cardTextColor,
          '--bio-text-color': cardBioColor,
          '--card-border-width': cardBorderWidth,
          '--card-border-color': cardBorderColor,
          '--card-border-style': cardBorderStyle,
          '--card-border-radius': cardBorderRadius,
          '--card-padding': cardPadding,
          '--card-background-color': cardBackgroundColor,
          '--social-fill-color': cardSocialFill,
          '--name-font-family': computeFont(nameFontFamily),
          '--name-font-size': nameFontSize,
          '--name-text-transform': nameTextTransform,
          '--bio-font-family': computeFont(bioFontFamily),
          '--bio-font-size': bioFontSize,
          '--bio-text-transform': bioTextTransform,
          '--alignment': alignment,
        } as CSSProperties
      }
      {...attributes}
    >
      {authors?.map((author: Author) => {
        if (isLive && shouldNavigateToAuthorPage) {
          return cardStructureV2 ? (
            <RedirectLink key={author.id} className={classnames('w-full text-left')} to={`/authors/${author.id}`}>
              <AuthorSerializer
                node={structureTiptapState?.content?.[0] as JSONContent & SerializableNode}
                rootClassName={'h-full'}
                rootStyle={{}}
                author={author}
              />
            </RedirectLink>
          ) : (
            <RedirectLink
              key={author.id}
              className={classnames(styles.authorCard, 'w-full text-left')}
              to={`/authors/${author.id}`}
              style={{
                boxShadow: shadow,
              }}
            >
              <Serializer author={author} attributes={element.attrs as TAuthorAttributes} />
            </RedirectLink>
          );
        }

        return cardStructureV2 ? (
          <div key={author.id} className={classnames(!isLive && 'pointer-events-none')}>
            <AuthorSerializer
              node={structureTiptapState?.content?.[0] as JSONContent & SerializableNode}
              rootClassName={'h-full'}
              rootStyle={{}}
              author={author}
            />
          </div>
        ) : (
          <div
            key={author.id}
            className={classnames(styles.authorCard, 'w-full text-left', !isLive && 'pointer-events-none')}
            style={{
              boxShadow: shadow,
            }}
          >
            <Serializer author={author} attributes={element.attrs as TAuthorAttributes} />
          </div>
        );
      })}

      {children}
    </div>
  );
}
